import * as CL from '@design-system/component-library';
import * as React from 'react';
import { addFilterMsg, closeMsg, emptyFiltersMsg, t, viewAllMsg } from '../../common/i18n/index.js';
import { desktopItemWidth } from '../../common/utils/gridUtils.js';
import { Anchor } from '../Anchor/Anchor.js';
import { LinkableAccordion } from '../LinkableAccordion/index.js';
import { MAX_DEFAULT_VISIBLE_OPTIONS } from './productGridFilterUtilsCoCo.js';
import type { DeviceProductOffer } from './productGridUtilsCoCo.js';

export enum FilterType {
  FILTER_BRAND = 'FILTER_BRAND',
  FILTER_PRICE = 'FILTER_PRICE',
}

export type Filters<T = DeviceProductOffer> = { [key in FilterType]: Filter<T> };

type Filter<T = DeviceProductOffer> = {
  displayText: () => string;
  options: Array<{ value: () => string; label?: string }>;
  filterFn: (offer: T, selectedOptions: string[]) => boolean;
  selectedOptions: string[];
  visible: boolean;
  hasTooManyOptions?: boolean;
};

interface ProductGridFilterHeaderCoCoProps {
  value: boolean;
  onChange: (newValue: boolean) => void;
}

export const ProductGridFilterHeaderCoCo = ({ onChange, value }: ProductGridFilterHeaderCoCoProps) => (
  <LinkableAccordion
    heading={t.G188(addFilterMsg)}
    id="product-filter-accordion-coco"
    defaultOpen={value}
    onOpen={() => onChange(!value)}
    onClose={() => onChange(!value)}
  />
);

interface ProductGridFilterStatusCoCoProps {
  filters: Filters;
  setFilters: (filters: Filters) => void;
}

export const ProductGridFilterStatusCoCo = ({ filters, setFilters }: ProductGridFilterStatusCoCoProps) => {
  const amount = Object.values(filters).flatMap(({ selectedOptions }) => selectedOptions).length;
  return (
    <a
      {...(amount > 0
        ? {
            className: 'of-product-grid__filter-status-link',
            onClick: () => {
              setFilters(
                Object.entries(filters)
                  .map(([key, value]) => ({ [key]: { ...value, selectedOptions: [] } as Filter }))
                  .reduce((acc, c) => ({ ...acc, ...c }), {}) as Filters
              );
            },
          }
        : { className: 'of-product-grid__filter-status-link disabled' })}
    >
      <span className="filter-amount">{amount}</span>
      <span className="filter-text">{t.IWVK(emptyFiltersMsg)}</span>
    </a>
  );
};

interface FilterElementCoCoProps<T> {
  filters: Filters<T>;
  onChange: (filters: Filters<T>) => void;
}

interface FilterOptionsElementCoCoProps<T> extends FilterElementCoCoProps<T> {
  filter: [string, Filter<T>];
  showAll: boolean;
}

const FilterOptionsElement = <T,>({ filter, filters, onChange, showAll }: FilterOptionsElementCoCoProps<T>) => (
  <>
    {filter[1].options
      .slice(0, showAll ? filter[1].options.length : Math.min(filter[1].options.length, MAX_DEFAULT_VISIBLE_OPTIONS))
      .map(option => (
        <CL.Checkbox
          id={option.value()}
          key={`${option.value()}-${filter[1].selectedOptions.includes(option.value()) ? 'checked' : 'unchecked'}`}
          value={option.value()}
          checked={filter[1].selectedOptions.includes(option.value())}
          onChange={event => {
            if (event.currentTarget.checked) {
              onChange({
                ...filters,
                [filter[0]]: {
                  ...filter[1],
                  selectedOptions: [...filter[1].selectedOptions, event.currentTarget.value],
                },
              });
            } else {
              onChange({
                ...filters,
                [filter[0]]: {
                  ...filter[1],
                  selectedOptions: filter[1].selectedOptions.filter(
                    selectedOption => selectedOption !== event.currentTarget.value
                  ),
                },
              });
            }
          }}
        >
          {option.label || option.value()}
        </CL.Checkbox>
      ))}
  </>
);

const FiltersElement = <T,>({ filters, onChange }: FilterElementCoCoProps<T>) => {
  const [showAll, onShowAll] = React.useState(false);
  const visibleFilters = Object.values(filters).filter(filter => filter.visible).length;
  const columnCount = visibleFilters < 5 ? 4 : 5;
  return (
    <CL.Grid>
      <CL.GridRow className="of-product-grid-filters">
        {Object.entries(filters)
          .filter(filter => filter[1].visible && filter[1].options.length > 0)
          .map(filter => (
            <CL.GridCol colsXS={4} colsS={4} colsM={6} colsL={desktopItemWidth(columnCount)} key={filter[0]}>
              <fieldset>
                <legend>{filter[1].displayText()}</legend>
                <FilterOptionsElement filter={filter} filters={filters} onChange={onChange} showAll={showAll} />
              </fieldset>
              {filter[1].hasTooManyOptions && !showAll && (
                <Anchor className="of-product-grid__filter-content__show-all" onClick={() => onShowAll(true)}>
                  {t.SF4C(viewAllMsg)}
                </Anchor>
              )}
            </CL.GridCol>
          ))}
      </CL.GridRow>
    </CL.Grid>
  );
};

interface ProductGridFilterCoCoProps<T> extends FilterElementCoCoProps<T> {
  onClose: () => void;
}

export const ProductGridFilterCoCo = <T,>({ filters, onChange, onClose }: ProductGridFilterCoCoProps<T>) => (
  <>
    <div className="of-product-grid__filter-content--mobile">
      <label className="of-product-grid__filter-content__label">{t.G188(addFilterMsg)}</label>
      <CL.Button
        aria-label={t.WOYD(closeMsg)}
        className="of-product-grid__filter-content__close"
        onClick={onClose}
        type="button"
        color="secondary"
      >
        <CL.Icon size="m" icon="close" aria-hidden="true" />
      </CL.Button>
    </div>
    <FiltersElement filters={filters} onChange={onChange} />
  </>
);
